import {
  OfferPricingType,
  OffersQuery as OffersQueryGQL,
  PercentageType,
  OfferType,
} from 'src/common/apollo/types/swarmx/graphql'
import { AnyToken, ERC20, NFT } from 'src/common/types/models/Tokens'
import { denormalize } from 'src/common/utils/big-utils'
import {
  bigInt,
  fullMulDiv,
  safeDivBigInt,
} from 'src/common/utils/bigint-utils'
import Big from 'big.js'
import { isNFTType } from '../web3/tokens'

export namespace AssetHelper {
  export const BPS = BigInt(10) ** BigInt(27)
  const SCALING_FACTOR = 10000n
  const PRICE_FEED_DECIMALS = 8

  export function standardize(
    amount: string | number | bigint,
    decimals: number
  ) {
    const _amount = bigInt(amount)
    return fullMulDiv(_amount, BPS, BigInt(10) ** BigInt(decimals))
  }

  export function unstandardize(
    amount: string | number | bigint,
    decimals?: number
  ) {
    const _amount = bigInt(amount)
    if (!decimals) return _amount
    return fullMulDiv(_amount, BigInt(10) ** BigInt(decimals), BPS)
  }

  export function bigToDenormalizedBI(amount: Big, asset: AnyToken) {
    return isNFTType(asset)
      ? bigInt(amount.toFixed(0))
      : bigInt(denormalize(amount, asset.decimals).toFixed(0))
  }

  export const calcUnitPrice = (amountA: bigint, amountB: bigint) => {
    return fullMulDiv(amountA, BPS, amountB)
  }

  export const calcPriceAPerB = (
    amountA: string | bigint,
    amountB: string | bigint,
    decimalsA?: number | null,
    decimalsB?: number | null
  ) => {
    let _amountA = bigInt(amountA)
    let _amountB = bigInt(amountB)
    if (decimalsA && decimalsB) {
      _amountA = standardize(amountA, decimalsA)
      _amountB = standardize(amountB, decimalsB)
      return calcUnitPrice(_amountB, _amountA)
    }
    return calcUnitPrice(_amountB, _amountA)
  }

  export const calcAmountToTake = (
    amountIn: bigint,
    pricePerDeposit: bigint,
    depositAsset: ERC20 | NFT,
    withdrawalAsset: ERC20 | NFT
  ) => {
    if (depositAsset.type === 'ERC20' && withdrawalAsset.type === 'ERC20') {
      const standardizedAmountIn = standardize(amountIn, depositAsset.decimals)

      const equivalentAmount = (standardizedAmountIn * pricePerDeposit) / BPS

      const amountToPay = unstandardize(
        equivalentAmount,
        withdrawalAsset.decimals
      )

      return amountToPay
    } else {
      return amountIn * pricePerDeposit
    }
  }

  export const calculateAvailableAmountOut = (
    amountIn: bigint,
    pricePer: bigint,
    offer: OffersQueryGQL['offers'][0],
    depositAsset: ERC20 | NFT,
    withdrawalAsset: ERC20 | NFT
  ) => {
    if (
      offer.offerType === OfferType.BlockOffer ||
      offer.offerPrice.pricingType === OfferPricingType.DynamicPricing
    ) {
      return bigInt(offer.amountOut)
    } // We don't handle NFTs assuming that all NFT offers are block offers

    let availableAmountOut = 0n
    if (depositAsset.type === 'ERC20' && withdrawalAsset.type === 'ERC20') {
      const standardizedAmountIn = standardize(amountIn, depositAsset.decimals)
      const equivalentAmount = (standardizedAmountIn * BPS) / pricePer
      availableAmountOut = unstandardize(
        equivalentAmount,
        withdrawalAsset.decimals
      )
    }

    return availableAmountOut
  }

  const calculatePercentage = (value: bigint, percentage: bigint) => {
    return fullMulDiv(value, percentage, SCALING_FACTOR)
  }

  export const calcRateWithPercentage = (
    rate: bigint,
    offerPercentage: bigint,
    percentageType: PercentageType
  ) => {
    const percentage = calculatePercentage(rate, offerPercentage)
    const rateWithPercentage =
      percentageType == PercentageType.Plus
        ? rate + percentage
        : rate - percentage
    return rateWithPercentage
  }

  export function calcRateAndPrice( // Only for dynamic offers
    depositAsset: ERC20 | NFT,
    withdrawalAsset: ERC20 | NFT,
    offer: OffersQueryGQL['offers'][0],
    pricePerUnitSell: number,
    pricePerUnitBuy: number
  ) {
    const depositAssetPriceInUsd = denormalize(
      pricePerUnitSell,
      PRICE_FEED_DECIMALS
    ).toFixed(0)
    const withdrawalAssetPriceInUsd = denormalize(
      pricePerUnitBuy,
      PRICE_FEED_DECIMALS
    ).toFixed(0)

    const standardizedDepositPrice = standardize(
      depositAssetPriceInUsd,
      PRICE_FEED_DECIMALS
    )
    const standardizedWithdrawalPrice = standardize(
      withdrawalAssetPriceInUsd,
      PRICE_FEED_DECIMALS
    )

    let depositToWithdrawalRate: bigint
    if (withdrawalAsset.type == 'ERC20') {
      let withdrawalDecimalFactor = bigInt(10 ** withdrawalAsset.decimals)
      depositToWithdrawalRate = fullMulDiv(
        standardizedDepositPrice,
        withdrawalDecimalFactor,
        standardizedWithdrawalPrice
      )
    } else {
      depositToWithdrawalRate = fullMulDiv(
        standardizedDepositPrice,
        BPS,
        standardizedWithdrawalPrice
      )
    }

    if (offer.offerPrice.percentage && offer.offerPrice.percentageType) {
      depositToWithdrawalRate = calcRateWithPercentage(
        depositToWithdrawalRate,
        bigInt(offer.offerPrice.percentage),
        offer.offerPrice.percentageType
      )
    }

    let withdrawalAmountPaid: bigint
    if (depositAsset.type !== 'ERC20') {
      withdrawalAmountPaid = depositToWithdrawalRate * bigInt(offer.amountIn)
    } else {
      let depositDecimalFactor = bigInt(10 ** depositAsset.decimals)
      withdrawalAmountPaid = fullMulDiv(
        depositToWithdrawalRate,
        bigInt(offer.amountIn),
        depositDecimalFactor
      )
    }

    if (withdrawalAsset.type === 'ERC1155') {
      withdrawalAmountPaid = safeDivBigInt(withdrawalAmountPaid, BPS)
    } else if (withdrawalAsset.type === 'ERC721') {
      withdrawalAmountPaid = bigInt(1)
    }

    return {
      withdrawalAmountPaid,
      depositToWithdrawalRate,
    }
  }

  export const getPriceAPerB = (
    assetIn: ERC20 | NFT,
    assetOut: ERC20 | NFT,
    amountIn: string | bigint,
    amountOut: string | bigint
  ): bigint => {
    if (assetIn.type === 'ERC20' && assetOut.type === 'ERC20') {
      const pricePerA = calcPriceAPerB(
        amountOut,
        amountIn,
        assetOut.decimals,
        assetIn.decimals
      )
      return pricePerA
    } else if (assetIn.type === 'ERC20') {
      const pricePerA = calcPriceAPerB(amountOut, amountIn) / BPS
      return pricePerA
    } else {
      const pricePerA = calcPriceAPerB(amountOut, amountIn)
      return pricePerA
    }
  }
}
